import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Users, Flame, Trophy, TrendingUp, Star, Shield, 
  ChevronRight, Search, Filter, Crown, Zap, Award,
  ArrowUp, Target, BarChart3, Clock, Check, Heart, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { useApp } from "../contexts/AppContext";
import { teamsAPI } from "../services/api";
import { 
  DEMO_TEAMS, DEMO_THEMATIC_TEAMS, TEAM_TYPES, TEAM_TYPE_LABELS,
  TEAM_TIERS, getTeamTier, TEAM_CONFIG, TEAM_BURN_MESSAGES,
  getMemberContributionPercent, TEAM_REWARDS, TEAM_BADGES
} from "../constants/teams";

// Team Card Component
const TeamCard = ({ team, onJoin, isJoined = false }) => {
  const tier = getTeamTier(team.score || 0);
  const typeInfo = TEAM_TYPE_LABELS[team.type] || TEAM_TYPE_LABELS['recovery'] || { label: 'Team', icon: '🔥', color: 'orange' };
  
  return (
    <Card className={`bg-[#1A1A1A] border-white/5 overflow-hidden hover:border-orange-500/30 transition-all group`}>
      {/* Team Image Header */}
      <div className="relative h-32 overflow-hidden">
        <img src={team.image} alt={team.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-black/50 to-transparent" />
        
        {/* Tier Badge */}
        <div className={`absolute top-3 left-3 px-2 py-1 rounded-lg bg-gradient-to-r ${tier.gradient} flex items-center gap-1`}>
          <span className="text-sm">{tier.icon}</span>
          <span className="text-white text-xs font-bold">{tier.name}</span>
        </div>
        
        {/* Type Badge */}
        <Badge className={`absolute top-3 right-3 bg-orange-500/20 text-orange-400 border-0 text-xs`}>
          {typeInfo.icon} {typeInfo.label}
        </Badge>
        
        {/* Rank */}
        <div className="absolute bottom-3 right-3 text-white/80 text-sm font-bold">
          #{team.rank}
        </div>
      </div>
      
      <CardContent className="p-4 space-y-4">
        {/* Team Name & Icon */}
        <div className="flex items-center gap-3">
          <span className="text-2xl">{team.icon}</span>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold truncate">{team.name}</h3>
            {team.originalProject && (
              <p className="text-white/40 text-xs truncate">from {team.originalProject}</p>
            )}
          </div>
        </div>
        
        {/* Description */}
        <p className="text-white/50 text-sm line-clamp-2">{team.description}</p>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2 bg-white/5 rounded-lg text-center">
            <p className="text-white font-bold text-sm">{team.memberCount.toLocaleString()}</p>
            <p className="text-white/40 text-[10px]">Members</p>
          </div>
          <div className="p-2 bg-orange-500/10 rounded-lg text-center">
            <p className="text-orange-400 font-bold text-sm">{(team.totalXP / 1000).toFixed(0)}K</p>
            <p className="text-white/40 text-[10px]">Total XP</p>
          </div>
          <div className="p-2 bg-red-500/10 rounded-lg text-center">
            <p className="text-red-400 font-bold text-sm">{(team.totalBurnzBurned / 1000).toFixed(0)}K</p>
            <p className="text-white/40 text-[10px]">Burned 🔥</p>
          </div>
        </div>
        
        {/* Weekly Performance */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-3 h-3 text-green-400" />
            <span className="text-white/50">This week:</span>
            <span className="text-green-400">+{(team.weeklyXP / 1000).toFixed(0)}K XP</span>
          </div>
          <span className="text-red-400">🔥 {(team.weeklyBurns / 1000).toFixed(1)}K</span>
        </div>
        
        {/* Team Score Bar */}
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-white/50">Team Score</span>
            <span className={`text-${tier.color}-400 font-bold`}>{team.score.toLocaleString()}</span>
          </div>
          <Progress value={Math.min((team.score / 50000) * 100, 100)} className="h-1.5" />
        </div>
        
        {/* Badges */}
        {team.badges && team.badges.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {team.badges.slice(0, 4).map(badgeId => {
              const badge = TEAM_BADGES.find(b => b.id === badgeId);
              return badge ? (
                <span key={badgeId} className="text-xs bg-white/5 px-2 py-0.5 rounded" title={badge.name}>
                  {badge.icon}
                </span>
              ) : null;
            })}
            {team.badges.length > 4 && (
              <span className="text-white/30 text-xs">+{team.badges.length - 4}</span>
            )}
          </div>
        )}
        
        {/* Action Button */}
        <Button 
          className={`w-full ${isJoined ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-purple-500 hover:bg-purple-600 text-white'}`}
          onClick={() => onJoin(team)}
          data-testid={`join-team-${team.id}-btn`}
        >
          {isJoined ? (
            <>
              <Check className="w-4 h-4 mr-2" />Joined
            </>
          ) : (
            <>
              <Users className="w-4 h-4 mr-2" />Join Team
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

// Team Leaderboard Row
const TeamLeaderboardRow = ({ team, rank }) => {
  const tier = getTeamTier(team.score);
  
  return (
    <div className={`flex items-center gap-4 p-3 rounded-xl transition-all ${
      rank === 1 ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20' :
      rank === 2 ? 'bg-slate-500/10 border border-slate-500/20' :
      rank === 3 ? 'bg-orange-600/10 border border-orange-600/20' :
      'bg-white/5'
    }`}>
      {/* Rank */}
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
        rank === 1 ? 'bg-yellow-500 text-black' :
        rank === 2 ? 'bg-slate-400 text-black' :
        rank === 3 ? 'bg-orange-600 text-white' :
        'bg-white/10 text-white'
      }`}>
        {rank}
      </div>
      
      {/* Team Info */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className="text-xl">{team.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{team.name}</p>
          <div className="flex items-center gap-2">
            <Badge className={`bg-${tier.color}-500/20 text-${tier.color}-400 border-0 text-[10px]`}>
              {tier.icon} {tier.name}
            </Badge>
            <span className="text-white/30 text-xs">{team.memberCount.toLocaleString()} members</span>
          </div>
        </div>
      </div>
      
      {/* Stats */}
      <div className="flex items-center gap-6">
        <div className="text-right">
          <p className="text-orange-400 font-bold">{(team.totalXP / 1000).toFixed(0)}K</p>
          <p className="text-white/30 text-[10px]">Total XP</p>
        </div>
        <div className="text-right">
          <p className="text-red-400 font-bold">{(team.totalBurnzBurned / 1000).toFixed(0)}K 🔥</p>
          <p className="text-white/30 text-[10px]">Burned</p>
        </div>
        <div className="text-right">
          <p className={`text-${tier.color}-400 font-bold`}>{team.score.toLocaleString()}</p>
          <p className="text-white/30 text-[10px]">Score</p>
        </div>
      </div>
    </div>
  );
};

// Main Teams Page
export const TeamsPage = () => {
  const { user } = useApp();
  const [activeTab, setActiveTab] = useState("recovery");
  const [searchQuery, setSearchQuery] = useState("");
  // User's joined team (from localStorage or state)
  const [joinedTeams, setJoinedTeams] = useState(() => {
    const stored = localStorage.getItem('burnz_joined_teams');
    if (stored) {
      try { return JSON.parse(stored); } catch { return ['team_dead_apes']; }
    }
    return ['team_dead_apes']; // Default to joined team for demo
  });
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiTeams, setApiTeams] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [joining, setJoining] = useState(false);
  
  const tabs = [
    { id: "recovery", label: "Recovery Teams", icon: "🧨", description: "Rugged project survivors" },
    { id: "thematic", label: "Community Teams", icon: "⚔️", description: "Cross-project collaboration" },
    { id: "my-team", label: "My Team", icon: "👤", description: "Your team dashboard" },
  ];
  
  // Fetch teams from API
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoading(true);
        const [recoveryData, thematicData, leaderboard] = await Promise.all([
          teamsAPI.getAll('recovery'),
          teamsAPI.getAll('thematic'),
          teamsAPI.getLeaderboard('all')
        ]);
        
        // If API returns data, use it; otherwise fall back to mock
        if (recoveryData.teams?.length > 0 || thematicData.teams?.length > 0) {
          setApiTeams({
            recovery: recoveryData.teams || [],
            thematic: thematicData.teams || []
          });
        }
        
        if (leaderboard.leaderboard?.length > 0) {
          setLeaderboardData(leaderboard.leaderboard);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeams();
  }, []);
  
  // Get teams based on active tab (API or mock)
  const currentTeams = activeTab === "recovery" 
    ? (apiTeams?.recovery?.length > 0 ? apiTeams.recovery : DEMO_TEAMS)
    : activeTab === "thematic" 
      ? (apiTeams?.thematic?.length > 0 ? apiTeams.thematic : DEMO_THEMATIC_TEAMS)
      : [];
  
  // Filter by search
  const filteredTeams = currentTeams.filter(team => 
    team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (team.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (team.originalProject || team.original_project_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Combined teams for leaderboard (API or mock)
  const allTeams = leaderboardData?.length > 0 
    ? leaderboardData.map((team, i) => ({ ...team, rank: i + 1, score: team.team_score || team.score || 0 }))
    : [...DEMO_TEAMS, ...DEMO_THEMATIC_TEAMS]
        .sort((a, b) => b.score - a.score)
        .map((team, i) => ({ ...team, rank: i + 1 }));
  
  const handleJoinTeam = async (team) => {
    if (joinedTeams.includes(team.id)) {
      // Leave team
      try {
        setJoining(true);
        await teamsAPI.leave(team.id);
        const newJoined = joinedTeams.filter(id => id !== team.id);
        setJoinedTeams(newJoined);
        localStorage.setItem('burnz_joined_teams', JSON.stringify(newJoined));
      } catch (error) {
        // Fall back to local state
        const newJoined = joinedTeams.filter(id => id !== team.id);
        setJoinedTeams(newJoined);
        localStorage.setItem('burnz_joined_teams', JSON.stringify(newJoined));
      } finally {
        setJoining(false);
      }
    } else {
      // Join team (replace existing - only 1 active team allowed)
      try {
        setJoining(true);
        await teamsAPI.join(team.id);
        const newJoined = [team.id]; // Only one team at a time
        setJoinedTeams(newJoined);
        localStorage.setItem('burnz_joined_teams', JSON.stringify(newJoined));
      } catch (error) {
        // Fall back to local state
        const newJoined = [team.id];
        setJoinedTeams(newJoined);
        localStorage.setItem('burnz_joined_teams', JSON.stringify(newJoined));
      } finally {
        setJoining(false);
      }
    }
  };
  
  // User's joined team (mock)
  const userTeam = joinedTeams.length > 0 
    ? allTeams.find(t => t.id === joinedTeams[0]) 
    : null;
  
  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-white flex items-center gap-2 lg:gap-3">
            <Shield className="w-5 h-5 lg:w-7 lg:h-7 text-purple-400" />
            Team Cards
          </h1>
          <p className="text-white/50 text-xs lg:text-sm mt-0.5 lg:mt-1">
            Join a team. Compete together. Burn together.
          </p>
        </div>
        <div className="flex items-center gap-2 lg:gap-3">
          <Button 
            variant="outline" 
            size="sm"
            className={`border-white/10 text-xs lg:text-sm ${showLeaderboard ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'text-white/70'}`}
            onClick={() => setShowLeaderboard(!showLeaderboard)}
            data-testid="toggle-leaderboard-btn"
          >
            <Trophy className="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1.5 lg:mr-2" />
            Leaderboard
          </Button>
        </div>
      </div>
      
      {/* How Teams Work Banner */}
      <Card className="bg-gradient-to-r from-purple-500/10 via-orange-500/10 to-red-500/10 border-purple-500/20">
        <CardContent className="p-3 lg:p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6">
            <div className="flex-1">
              <h3 className="text-white font-bold text-sm lg:text-base mb-1">How Team Cards Work</h3>
              <p className="text-white/60 text-xs lg:text-sm">
                Teams compete through <span className="text-orange-400">XP earned</span> and <span className="text-red-400">BURNZ burned</span>. 
                Top teams earn <span className="text-purple-400">prestige badges</span> and <span className="text-cyan-400">reduced burn fees</span>.
              </p>
            </div>
            <div className="flex gap-2 lg:gap-4 text-center">
              <div className="p-2 lg:p-3 bg-white/5 rounded-xl flex-1 lg:flex-initial">
                <p className="text-lg lg:text-2xl mb-0.5 lg:mb-1">🏆</p>
                <p className="text-white/50 text-[10px] lg:text-xs">Prestige</p>
              </div>
              <div className="p-2 lg:p-3 bg-white/5 rounded-xl flex-1 lg:flex-initial">
                <p className="text-lg lg:text-2xl mb-0.5 lg:mb-1">⚡</p>
                <p className="text-white/50 text-[10px] lg:text-xs">XP Bonus</p>
              </div>
              <div className="p-2 lg:p-3 bg-white/5 rounded-xl flex-1 lg:flex-initial">
                <p className="text-lg lg:text-2xl mb-0.5 lg:mb-1">🔥</p>
                <p className="text-white/50 text-[10px] lg:text-xs">Burn Impact</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Global Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Total Teams", value: allTeams.length, icon: Shield, color: "purple" },
          { label: "Total Members", value: `${(allTeams.reduce((sum, t) => sum + t.memberCount, 0) / 1000).toFixed(1)}K`, icon: Users, color: "blue" },
          { label: "XP This Week", value: `${(allTeams.reduce((sum, t) => sum + t.weeklyXP, 0) / 1000000).toFixed(1)}M`, icon: Zap, color: "orange" },
          { label: "Burned This Week", value: `${(allTeams.reduce((sum, t) => sum + t.weeklyBurns, 0) / 1000).toFixed(0)}K 🔥`, icon: Flame, color: "red" },
        ].map((stat, i) => (
          <Card key={i} className="bg-[#1A1A1A] border-white/5">
            <CardContent className="p-4">
              <stat.icon className={`w-5 h-5 text-${stat.color}-400 mb-2`} />
              <p className="text-xl font-bold text-white">{stat.value}</p>
              <p className="text-white/50 text-xs">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Leaderboard View */}
      {showLeaderboard && (
        <Card className="bg-[#1A1A1A] border-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              Team Leaderboard
            </CardTitle>
            <CardDescription className="text-white/50">
              Ranked by Team Success Score = (XP × 0.4) + (Burns × 0.4) + (Active × 0.2)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {allTeams.slice(0, 10).map((team, i) => (
              <TeamLeaderboardRow key={team.id} team={team} rank={i + 1} />
            ))}
          </CardContent>
        </Card>
      )}
      
      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-white/10 pb-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-all ${
              activeTab === tab.id 
                ? "text-purple-400 bg-purple-500/10 border-b-2 border-purple-500" 
                : "text-white/50 hover:text-white"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <span className="text-base">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
        
        {/* Search */}
        <div className="ml-auto relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input 
            placeholder="Search teams..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-white/5 border-white/10 pl-10 text-white placeholder:text-white/30"
            data-testid="search-teams-input"
          />
        </div>
      </div>
      
      {/* Recovery Teams Tab */}
      {activeTab === "recovery" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                🧨 Recovery Teams
              </h2>
              <p className="text-white/50 text-sm">For survivors of rugged projects. Find your community.</p>
            </div>
            <Badge className="bg-orange-500/20 text-orange-400 border-0">
              Phase 1: Admin-Created
            </Badge>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTeams.map(team => (
              <TeamCard 
                key={team.id} 
                team={team} 
                onJoin={handleJoinTeam}
                isJoined={joinedTeams.includes(team.id)}
              />
            ))}
          </div>
          
          {filteredTeams.length === 0 && (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No teams found matching your search</p>
            </div>
          )}
        </div>
      )}
      
      {/* Thematic Teams Tab */}
      {activeTab === "thematic" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                ⚔️ Community Teams
              </h2>
              <p className="text-white/50 text-sm">Cross-project collaboration and identity beyond recovery.</p>
            </div>
            <Badge className="bg-purple-500/20 text-purple-400 border-0">
              Phase 2: Coming Soon
            </Badge>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {DEMO_THEMATIC_TEAMS.map(team => (
              <TeamCard 
                key={team.id} 
                team={team} 
                onJoin={handleJoinTeam}
                isJoined={joinedTeams.includes(team.id)}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* My Team Tab */}
      {activeTab === "my-team" && (
        <div className="space-y-6">
          {userTeam ? (
            <>
              {/* User's Team Dashboard */}
              <Card className={`bg-gradient-to-br from-${getTeamTier(userTeam.score).color}-900/30 via-[#1A1A1A] to-[#1A1A1A] border-${getTeamTier(userTeam.score).color}-500/30 overflow-hidden`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-6">
                    {/* Team Icon */}
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-purple-500 to-orange-500 flex items-center justify-center text-5xl shadow-xl">
                      {userTeam.icon}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-2xl font-bold text-white">{userTeam.name}</h2>
                        <Badge className={`bg-${getTeamTier(userTeam.score).color}-500/20 text-${getTeamTier(userTeam.score).color}-400 border-0`}>
                          {getTeamTier(userTeam.score).icon} {getTeamTier(userTeam.score).name}
                        </Badge>
                        <Badge className="bg-white/10 text-white/70 border-0">Rank #{userTeam.rank}</Badge>
                      </div>
                      <p className="text-white/60 mb-4">{userTeam.description}</p>
                      
                      {/* Team Stats */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="p-3 bg-white/5 rounded-xl text-center">
                          <p className="text-2xl font-bold text-white">{userTeam.memberCount.toLocaleString()}</p>
                          <p className="text-white/50 text-xs">Members</p>
                        </div>
                        <div className="p-3 bg-orange-500/10 rounded-xl text-center">
                          <p className="text-2xl font-bold text-orange-400">{(userTeam.totalXP / 1000000).toFixed(2)}M</p>
                          <p className="text-white/50 text-xs">Total XP</p>
                        </div>
                        <div className="p-3 bg-red-500/10 rounded-xl text-center">
                          <p className="text-2xl font-bold text-red-400">{(userTeam.totalBurnzBurned / 1000).toFixed(0)}K</p>
                          <p className="text-white/50 text-xs">Total Burned 🔥</p>
                        </div>
                        <div className="p-3 bg-purple-500/10 rounded-xl text-center">
                          <p className="text-2xl font-bold text-purple-400">{userTeam.score.toLocaleString()}</p>
                          <p className="text-white/50 text-xs">Team Score</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Your Contribution */}
              <Card className="bg-[#1A1A1A] border-white/5">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-orange-400" />
                    Your Contribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <p className="text-3xl font-bold text-orange-400">{(user?.xp || 2847).toLocaleString()}</p>
                      <p className="text-white/50 text-sm">Your XP</p>
                      <p className="text-green-400 text-xs mt-1">
                        {((user?.xp || 2847) / userTeam.totalXP * 100).toFixed(2)}% of team
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <p className="text-3xl font-bold text-red-400">{(user?.totalBurnzBurned || 450).toLocaleString()}</p>
                      <p className="text-white/50 text-sm">Your Burns 🔥</p>
                      <p className="text-green-400 text-xs mt-1">
                        {getMemberContributionPercent(user?.totalBurnzBurned || 450, userTeam.totalBurnzBurned)}% of team
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <p className="text-3xl font-bold text-purple-400">Active</p>
                      <p className="text-white/50 text-sm">Status</p>
                      <p className="text-green-400 text-xs mt-1">Contributing member</p>
                    </div>
                  </div>
                  
                  {/* Burn Impact Message */}
                  <div className="mt-4 p-4 bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-xl border border-red-500/20">
                    <p className="text-white/80 text-sm flex items-center gap-2">
                      <Flame className="w-5 h-5 text-red-400" />
                      {TEAM_BURN_MESSAGES.personal(user?.totalBurnzBurned || 450)}
                      <span className="text-orange-400 font-medium">
                        {TEAM_BURN_MESSAGES.teamContribution(getMemberContributionPercent(user?.totalBurnzBurned || 450, userTeam.totalBurnzBurned))}
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Team Badges */}
              {userTeam.badges && userTeam.badges.length > 0 && (
                <Card className="bg-[#1A1A1A] border-white/5">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Award className="w-5 h-5 text-yellow-400" />
                      Team Badges
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {userTeam.badges.map(badgeId => {
                        const badge = TEAM_BADGES.find(b => b.id === badgeId);
                        if (!badge) return null;
                        return (
                          <div key={badgeId} className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-xl">
                            <span className="text-2xl">{badge.icon}</span>
                            <div>
                              <p className="text-white font-medium text-sm">{badge.name}</p>
                              <p className="text-white/40 text-xs">{badge.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Team Achievements & Milestones */}
              <Card className="bg-[#1A1A1A] border-white/5">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-yellow-400" />
                        Team Achievements
                      </CardTitle>
                      <CardDescription className="text-white/50">
                        Collective milestones unlock rewards for the entire team
                      </CardDescription>
                    </div>
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
                      {userTeam.badges?.length || 0} Unlocked
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Achievement Categories */}
                  <div className="space-y-4">
                    {/* Member Milestones */}
                    <div>
                      <h4 className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Member Milestones
                      </h4>
                      <div className="grid grid-cols-4 gap-3">
                        {[
                          { id: 'team_first_100', target: 100, icon: '💯', name: 'Century Club' },
                          { id: 'team_first_500', target: 500, icon: '🏰', name: 'Battalion' },
                          { id: 'team_first_1000', target: 1000, icon: '⚔️', name: 'Legion' },
                          { id: 'team_first_3000', target: 3000, icon: '🏛️', name: 'Army' },
                        ].map(milestone => {
                          const isUnlocked = userTeam.badges?.includes(milestone.id);
                          const progress = Math.min((userTeam.memberCount / milestone.target) * 100, 100);
                          return (
                            <div 
                              key={milestone.id} 
                              className={`p-3 rounded-xl border ${isUnlocked ? 'bg-green-500/10 border-green-500/30' : 'bg-white/5 border-white/10'}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-2xl">{milestone.icon}</span>
                                {isUnlocked && <Check className="w-4 h-4 text-green-400" />}
                              </div>
                              <p className={`font-medium text-sm ${isUnlocked ? 'text-green-400' : 'text-white'}`}>{milestone.name}</p>
                              <p className="text-white/40 text-xs mb-2">{milestone.target.toLocaleString()} members</p>
                              {!isUnlocked && (
                                <>
                                  <Progress value={progress} className="h-1 mb-1" />
                                  <p className="text-white/30 text-[10px]">{userTeam.memberCount}/{milestone.target}</p>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Burn Milestones */}
                    <div>
                      <h4 className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
                        <Flame className="w-4 h-4 text-red-400" />
                        Burn Milestones
                      </h4>
                      <div className="grid grid-cols-4 gap-3">
                        {[
                          { id: 'team_burn_10k', target: 10000, icon: '🔥', name: 'Kindling', color: 'orange' },
                          { id: 'team_burn_100k', target: 100000, icon: '🔥', name: 'Bonfire', color: 'orange' },
                          { id: 'team_burn_1m', target: 1000000, icon: '🔥', name: 'Inferno', color: 'red' },
                          { id: 'team_burn_10m', target: 10000000, icon: '☄️', name: 'Supernova', color: 'purple' },
                        ].map(milestone => {
                          const isUnlocked = userTeam.badges?.includes(milestone.id);
                          const progress = Math.min((userTeam.totalBurnzBurned / milestone.target) * 100, 100);
                          return (
                            <div 
                              key={milestone.id} 
                              className={`p-3 rounded-xl border ${isUnlocked ? `bg-${milestone.color}-500/10 border-${milestone.color}-500/30` : 'bg-white/5 border-white/10'}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-2xl">{milestone.icon}</span>
                                {isUnlocked && <Check className={`w-4 h-4 text-${milestone.color}-400`} />}
                              </div>
                              <p className={`font-medium text-sm ${isUnlocked ? `text-${milestone.color}-400` : 'text-white'}`}>{milestone.name}</p>
                              <p className="text-white/40 text-xs mb-2">{(milestone.target / 1000).toLocaleString()}K BURNZ</p>
                              {!isUnlocked && (
                                <>
                                  <Progress value={progress} className="h-1 mb-1" />
                                  <p className="text-white/30 text-[10px]">{(userTeam.totalBurnzBurned / 1000).toFixed(0)}K/{(milestone.target / 1000).toLocaleString()}K</p>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* XP Milestones */}
                    <div>
                      <h4 className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-cyan-400" />
                        XP Milestones
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { id: 'team_xp_100k', target: 100000, icon: '⚡', name: 'Grinders' },
                          { id: 'team_xp_1m', target: 1000000, icon: '⚡', name: 'Powerhouse' },
                          { id: 'team_xp_10m', target: 10000000, icon: '💪', name: 'Unstoppable' },
                        ].map(milestone => {
                          const isUnlocked = userTeam.badges?.includes(milestone.id);
                          const progress = Math.min((userTeam.totalXP / milestone.target) * 100, 100);
                          return (
                            <div 
                              key={milestone.id} 
                              className={`p-3 rounded-xl border ${isUnlocked ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-white/5 border-white/10'}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-2xl">{milestone.icon}</span>
                                {isUnlocked && <Check className="w-4 h-4 text-cyan-400" />}
                              </div>
                              <p className={`font-medium text-sm ${isUnlocked ? 'text-cyan-400' : 'text-white'}`}>{milestone.name}</p>
                              <p className="text-white/40 text-xs mb-2">{(milestone.target / 1000000).toFixed(1)}M XP</p>
                              {!isUnlocked && (
                                <>
                                  <Progress value={progress} className="h-1 mb-1" />
                                  <p className="text-white/30 text-[10px]">{(userTeam.totalXP / 1000000).toFixed(2)}M/{(milestone.target / 1000000).toFixed(1)}M</p>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Seasonal Achievements */}
                    <div>
                      <h4 className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
                        <Crown className="w-4 h-4 text-yellow-400" />
                        Seasonal Achievements
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { id: 'season_champion', icon: '🏆', name: 'Season Champion', desc: 'Win a season', rarity: 'mythic' },
                          { id: 'season_top3', icon: '🥇', name: 'Season Podium', desc: 'Top 3 in season', rarity: 'legendary' },
                          { id: 'top_burner_week', icon: '🔥', name: 'Weekly Flame', desc: 'Top burner of the week', rarity: 'rare' },
                        ].map(achievement => {
                          const isUnlocked = userTeam.badges?.includes(achievement.id);
                          const rarityColors = {
                            mythic: 'from-pink-500 to-purple-600',
                            legendary: 'from-yellow-500 to-orange-600',
                            rare: 'from-blue-500 to-cyan-600',
                          };
                          return (
                            <div 
                              key={achievement.id} 
                              className={`p-4 rounded-xl border ${isUnlocked ? `bg-gradient-to-br ${rarityColors[achievement.rarity]} border-transparent` : 'bg-white/5 border-white/10 opacity-60'}`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-3xl">{achievement.icon}</span>
                                <div>
                                  <p className={`font-bold ${isUnlocked ? 'text-white' : 'text-white/70'}`}>{achievement.name}</p>
                                  <p className="text-white/60 text-xs">{achievement.desc}</p>
                                  {!isUnlocked && (
                                    <p className="text-white/30 text-xs mt-1 italic">Not yet earned</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Next Milestone Highlight */}
                  <div className="p-4 bg-gradient-to-r from-purple-500/10 to-orange-500/10 rounded-xl border border-purple-500/20">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                        <Target className="w-6 h-6 text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">Next Milestone</p>
                        <p className="text-white/60 text-sm">
                          {userTeam.memberCount < 500 
                            ? `Reach 500 members to unlock "Battalion" badge (${500 - userTeam.memberCount} more needed)`
                            : userTeam.totalBurnzBurned < 100000 
                              ? `Burn 100K BURNZ to unlock "Bonfire" badge (${((100000 - userTeam.totalBurnzBurned) / 1000).toFixed(0)}K more needed)`
                              : userTeam.totalXP < 1000000 
                                ? `Earn 1M XP to unlock "Powerhouse" badge`
                                : 'All major milestones achieved! 🎉'
                          }
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-orange-400 font-bold">
                          {userTeam.memberCount < 500 
                            ? `${((userTeam.memberCount / 500) * 100).toFixed(0)}%`
                            : userTeam.totalBurnzBurned < 100000 
                              ? `${((userTeam.totalBurnzBurned / 100000) * 100).toFixed(0)}%`
                              : userTeam.totalXP < 1000000 
                                ? `${((userTeam.totalXP / 1000000) * 100).toFixed(0)}%`
                                : '100%'
                          }
                        </p>
                        <p className="text-white/40 text-xs">Progress</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            /* No Team Joined */
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardContent className="py-16 text-center">
                <Shield className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">You have not joined a team yet</h3>
                <p className="text-white/50 mb-6 max-w-md mx-auto">
                  Join a team to compete together, track your burn impact, and earn prestige rewards.
                </p>
                <Button 
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  onClick={() => setActiveTab("recovery")}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Browse Teams
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      
      {/* Team Tier Benefits */}
      <Card className="bg-[#1A1A1A] border-white/5">
        <CardHeader>
          <CardTitle className="text-white">Team Tier Benefits</CardTitle>
          <CardDescription className="text-white/50">
            As your team score increases, everyone benefits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3">
            {TEAM_TIERS.map(tier => (
              <div key={tier.id} className={`p-4 rounded-xl border border-${tier.color}-500/20 bg-${tier.color}-500/5`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{tier.icon}</span>
                  <span className="text-white font-bold">{tier.name}</span>
                </div>
                <p className="text-white/50 text-xs mb-2">{tier.minScore.toLocaleString()}+ score</p>
                <ul className="space-y-1">
                  {tier.benefits.map((benefit, i) => (
                    <li key={i} className="text-white/60 text-xs flex items-start gap-1">
                      <Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamsPage;
